import Phaser from 'phaser';

import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { BlackjackEvents, GameEvent, PileId, RoundResult } from 'game-logic';
import { PlayerMiniBetView } from '../player/player-mini-bet-view';
import { PlayerCardStackView } from '../player/player-card-stack-view';
import { PlayerHandViewModel } from './player-hand-view-model';
import { RoundedLabelView } from '../components/rounded-label-view';
import { ChipStackView } from '../chips/stack/chip-stack-view';
// import { DebugContainerVisual } from '../debug/debug-container-visual';
import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { UxMetrics } from '../../model/ux-metrics';
import { ChipStackViewModel } from '../chips/stack/chip-stack-view-model';

export class PlayerHandView extends Phaser.GameObjects.Container {
  public get playerId(): PileId {
    return this.viewModel.playerId;
  }

  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private playerMiniBetView: PlayerMiniBetView;
  private playerCardStackView: PlayerCardStackView;
  private playerScoreView: RoundedLabelView;
  private playerResultView: RoundedLabelView;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: PlayerHandViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.playerMiniBetView = new PlayerMiniBetView(scene, 0, 0, viewModel.playerMiniBetViewModel);
    this.add(this.playerMiniBetView);

    this.playerCardStackView = new PlayerCardStackView(
      scene,
      0,
      0,
      viewModel.playerCardStackViewModel,
    );
    this.add(this.playerCardStackView);

    this.playerScoreView = new RoundedLabelView(scene, 0, 0, viewModel.playerScoreViewModel);
    this.add(this.playerScoreView);

    this.playerResultView = new RoundedLabelView(scene, 0, 0, viewModel.playerResultViewModel);
    this.add(this.playerResultView);

    this.setName(PlayerHandView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      viewModel.scoreText$.subscribe((text) => {
        this.playerScoreView.setVisible(text.length > 0);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.resultText$.subscribe((text) => {
        // TODO: Nice-to-have would be to make this label fade in slightly

        this.playerResultView.setVisible(text.length > 0);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.betStackAnimationTrigger$.subscribe(() => {
        this.animateBetStack();
      }),
    );
    this.subscriptionGroup.add(
      viewModel.doubleStackAnimationTrigger$.subscribe(() => {
        this.animateDoubleStack();
      }),
    );
    this.subscriptionGroup.add(
      viewModel.splitStackAnimationTrigger$.subscribe(() => {
        this.animateSplitStack();
      }),
    );
    this.subscriptionGroup.add(
      viewModel.loseChipsAnimationTrigger$.subscribe((hadDoubled) => {
        this.animateLoseChips(hadDoubled);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.winChipsAnimationTrigger$.subscribe((payload) => {
        this.animateWinChips(payload.hasDoubled, payload.result);
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.playerMiniBetView?.onResize(resizeMetrics);
    this.playerCardStackView?.onResize(resizeMetrics);
    this.playerScoreView?.onResize(resizeMetrics);
    this.playerResultView?.onResize(resizeMetrics);

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const boxScale = 0.8;
    const controlsWidth = layoutConfig.bettingBoxWidth * boxScale * scalingMetrics.scaleFactor;
    const controlsHeight = layoutConfig.bettingBoxHeight * boxScale * scalingMetrics.scaleFactor;

    this.setSize(controlsWidth, controlsHeight);

    this.playerCardStackView.setPosition(0, 0);

    const padding = layoutConfig.paddingSm * scalingMetrics.scaleFactor;
    this.playerMiniBetView.setY(
      (controlsHeight + this.playerMiniBetView.displayHeight) * -0.5 - padding,
    );

    this.playerScoreView.setY(
      (controlsHeight + this.playerScoreView.displayHeight) * 0.5 + padding,
    );

    this.playerResultView.setY(
      (controlsHeight - this.playerResultView.displayHeight) * 0.5 - padding,
    );
  }

  public async handleLogicEvents(event: GameEvent<BlackjackEvents>): Promise<void> {
    // Don't process instant events
    if (event.instant) {
      return;
    }

    switch (event.type) {
      case 'onPlayerBet':
        this.viewModel.playerBet(event.payload.betAmount);
        break;
      case 'onDrawDeckPlayer':
        // Pass along the logic events to the card stack before updating score
        await this.playerCardStackView.handleLogicEvents(event);

        this.viewModel.updateScore(event.payload.player);
        break;
      case 'onBetDoubled':
        await this.viewModel.playerBetDoubled(event.payload.player);
        break;
      case 'onPlayerHandComplete':
        this.viewModel.updateScore(event.payload.player);
        break;
      case 'onPlayerCleared':
        this.viewModel.clearScore(event.payload.player);

        // Pass along the logic events to the card stack after clearing scoring
        await this.playerCardStackView.handleLogicEvents(event);
        break;
      case 'onSetResult':
        this.viewModel.setResult(event.payload.player, event.payload.result);
        break;
      default:
        break;
    }
  }

  private animateBetStack(): void {
    const tmpChipStackViewModel = this.viewModel.generateChipStackViewModel();
    const tmpChipStackView = new ChipStackView(this.scene, 0, 0, tmpChipStackViewModel).setDepth(
      1000,
    );
    if (this.currentResizeMetrics) {
      tmpChipStackView.onResize(this.currentResizeMetrics);
    }
    for (const chip of this.viewModel.interfaceController.currentBetChips) {
      tmpChipStackViewModel.chipStackIncreased(chip, false);
    }

    const startPoint = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(0, 0, startPoint);
    tmpChipStackView.setPosition(startPoint.x, startPoint.y);

    const endPoint = this.playerMiniBetView.chipGlobalPosition;

    this.scene.tweens.add({
      targets: [tmpChipStackView],
      x: endPoint.x,
      y: endPoint.y,
      scaleX: 0.33,
      scaleY: 0.33,
      duration: UxMetrics.ChipStackAppear,
      ease: Phaser.Math.Easing.Quartic.Out,
      onComplete: () => {
        tmpChipStackView.destroy();
      },
    });

    this.playerMiniBetView.setAlpha(0);

    this.scene.tweens.add({
      targets: [this.playerMiniBetView],
      alpha: 1,
      delay: UxMetrics.ChipStackAppear * 0.75,
      duration: UxMetrics.ChipStackAppear * 0.25,
      ease: Phaser.Math.Easing.Quartic.InOut,
      onComplete: () => {
        this.playerMiniBetView.setAlpha(1);
      },
    });
  }

  private animateDoubleStack(): void {
    const tmpChipStackViewModel = this.viewModel.generateChipStackViewModel();
    const tmpChipStackView = new ChipStackView(this.scene, 0, 0, tmpChipStackViewModel).setDepth(
      1000,
    );
    if (this.currentResizeMetrics) {
      tmpChipStackView.onResize(this.currentResizeMetrics);
    }
    for (const chip of this.viewModel.interfaceController.currentBetChips) {
      tmpChipStackViewModel.chipStackIncreased(chip, false);
    }

    const startPoint = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(0, this.height * 4, startPoint);
    tmpChipStackView.setPosition(startPoint.x, startPoint.y);

    const midPoint = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(0, this.height * 1.25, midPoint);

    this.scene.tweens.add({
      targets: [tmpChipStackView],
      x: midPoint.x,
      y: midPoint.y,
      duration: UxMetrics.ChipStackAppear,
      ease: Phaser.Math.Easing.Quartic.Out,
    });

    const endPoint = this.playerMiniBetView.chipGlobalPosition;

    this.scene.tweens.add({
      targets: [tmpChipStackView],
      x: endPoint.x,
      y: endPoint.y,
      scaleX: 0.33,
      scaleY: 0.33,
      delay: UxMetrics.ChipStackAppear,
      duration: UxMetrics.ChipStackTransition,
      ease: Phaser.Math.Easing.Quartic.InOut,
      onComplete: () => {
        tmpChipStackView.destroy();
      },
    });
  }

  // TODO: This is identical to above, we might be able to combine the two
  private animateSplitStack(): void {
    const tmpChipStackViewModel = this.viewModel.generateChipStackViewModel();
    const tmpChipStackView = new ChipStackView(this.scene, 0, 0, tmpChipStackViewModel).setDepth(
      1000,
    );
    if (this.currentResizeMetrics) {
      tmpChipStackView.onResize(this.currentResizeMetrics);
    }
    for (const chip of this.viewModel.interfaceController.currentBetChips) {
      tmpChipStackViewModel.chipStackIncreased(chip, false);
    }

    const startPoint = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(0, this.height * 4, startPoint);
    tmpChipStackView.setPosition(startPoint.x, startPoint.y);

    const midPoint = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(0, this.height * 1.25, midPoint);

    this.scene.tweens.add({
      targets: [tmpChipStackView],
      x: midPoint.x,
      y: midPoint.y,
      duration: UxMetrics.ChipStackAppear,
      ease: Phaser.Math.Easing.Quartic.Out,
    });

    const endPoint = this.playerMiniBetView.chipGlobalPosition;

    this.scene.tweens.add({
      targets: [tmpChipStackView],
      x: endPoint.x,
      y: endPoint.y,
      scaleX: 0.33,
      scaleY: 0.33,
      delay: UxMetrics.ChipStackAppear,
      duration: UxMetrics.ChipStackTransition,
      ease: Phaser.Math.Easing.Quartic.InOut,
      onComplete: () => {
        tmpChipStackView.destroy();
      },
    });
  }

  private animateLoseChips(hadDoubled: boolean): void {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const chipWidth = 180 * scaleFactor; // TODO: Add to globals somewhere

    const chipStackViewModel = this.viewModel.generateChipStackViewModel();
    const singleChipStackView = this.createChipStack(chipStackViewModel);
    const doubleCheckStackView = hadDoubled ? this.createChipStack(chipStackViewModel) : undefined;

    for (const chip of this.viewModel.interfaceController.currentBetChips) {
      chipStackViewModel.chipStackIncreased(chip, false);
    }

    this.setChipStacksInitialPosition([singleChipStackView, doubleCheckStackView]);

    const midPoint = this.globalCardStackMidpoint();
    const endPoint = this.globalDisposePosition();

    this.animateChipMovement(
      singleChipStackView,
      midPoint,
      endPoint,
      hadDoubled ? chipWidth * 0.5 : 0,
    );

    if (doubleCheckStackView) {
      this.animateChipMovement(doubleCheckStackView, midPoint, endPoint, chipWidth * -0.5);
    }

    // Also animate the mini bet view
    this.fadeOutMiniBetView();
  }

  private animateWinChips(hadDoubled: boolean, result: RoundResult): void {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const chipWidth = 180 * scaleFactor; // TODO: Add to globals somewhere

    const chipStackViewModel = this.viewModel.generateChipStackViewModel();
    const singleChipStackView = this.createChipStack(chipStackViewModel);
    const doubleCheckStackView = hadDoubled ? this.createChipStack(chipStackViewModel) : undefined;

    for (const chip of this.viewModel.interfaceController.currentBetChips) {
      chipStackViewModel.chipStackIncreased(chip, false);
    }

    this.setChipStacksInitialPosition([singleChipStackView, doubleCheckStackView]);

    const midPoint = this.globalCardStackMidpoint();
    const endPoint = this.globalBalancePosition();

    this.animateChipMovement(
      singleChipStackView,
      midPoint,
      endPoint,
      hadDoubled ? chipWidth * 0.5 : 0,
    );

    if (doubleCheckStackView) {
      this.animateChipMovement(doubleCheckStackView, midPoint, endPoint, chipWidth * -0.5);
    }

    // Also animate the mini bet view
    this.fadeOutMiniBetView();

    if (result === RoundResult.PlayerBlackjack || result === RoundResult.PlayerWin) {
      // Also give chips

      const winChipStackViewModel = this.viewModel.generateChipStackViewModel();
      const singleWinChipStackView = this.createChipStack(winChipStackViewModel);
      const doubleWinCheckStackView = hadDoubled
        ? this.createChipStack(winChipStackViewModel)
        : undefined;

      for (const chip of this.viewModel.interfaceController.currentBetChips) {
        winChipStackViewModel.chipStackIncreased(chip, false);
      }

      const winStartPoint = this.globalWinChipsPosition();
      singleWinChipStackView.setPosition(winStartPoint.x, winStartPoint.y);
      doubleWinCheckStackView?.setPosition(winStartPoint.x, winStartPoint.y);

      const winMidPoint = this.globalCardStackMidpoint();
      const winEndPoint = this.globalBalancePosition();

      this.animateChipMovement(
        singleWinChipStackView,
        winMidPoint,
        winEndPoint,
        hadDoubled ? chipWidth * 0.25 : chipWidth * -0.25,
        chipWidth * -0.5,
      );

      if (doubleWinCheckStackView) {
        this.animateChipMovement(
          doubleWinCheckStackView,
          winMidPoint,
          winEndPoint,
          chipWidth * -0.75,
          chipWidth * -0.5,
        );
      }
    }
  }

  private createChipStack(chipStackViewModel: ChipStackViewModel): ChipStackView {
    const chipStack = new ChipStackView(this.scene, 0, 0, chipStackViewModel).setDepth(1000);
    if (this.currentResizeMetrics) {
      chipStack.onResize(this.currentResizeMetrics);
    }
    return chipStack;
  }

  private setChipStacksInitialPosition(chipStacks: (ChipStackView | undefined)[]) {
    const startPoint = this.playerMiniBetView.chipGlobalPosition;
    chipStacks.forEach((view) => {
      view?.setScale(0.33, 0.33);
      view?.setPosition(startPoint.x, startPoint.y);
    });
  }

  private animateChipMovement(
    chipStack: ChipStackView,
    midPoint: Phaser.Math.Vector2,
    endPoint: Phaser.Math.Vector2,
    xOffset: number,
    yOffset = 0,
  ): void {
    this.scene.tweens.add({
      targets: chipStack,
      x: midPoint.x + xOffset,
      y: midPoint.y + yOffset,
      scaleX: 1,
      scaleY: 1,
      duration: UxMetrics.ResultChipCreation,
      ease: Phaser.Math.Easing.Quadratic.InOut,
    });

    this.scene.tweens.add({
      targets: chipStack,
      x: endPoint.x + xOffset,
      y: endPoint.y + yOffset,
      delay: UxMetrics.ResultChipCreation,
      duration: UxMetrics.ResultChipCollection,
      ease: Phaser.Math.Easing.Quartic.InOut,
      onComplete: () => chipStack.destroy(),
    });
  }

  private fadeOutMiniBetView(): void {
    this.scene.tweens.add({
      targets: this.playerMiniBetView,
      alpha: 0,
      delay: UxMetrics.ResultChipCreation * 0.2,
      duration: UxMetrics.ResultChipCreation * 0.4,
      ease: Phaser.Math.Easing.Quadratic.InOut,
    });
  }

  private globalCardStackMidpoint(): Phaser.Math.Vector2 {
    const midPoint = new Phaser.Math.Vector2();
    this.playerCardStackView.getWorldTransformMatrix().transformPoint(0, 0, midPoint);
    return midPoint;
  }

  private globalDisposePosition(): Phaser.Math.Vector2 {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const scaledOffsetX = this.currentResizeMetrics?.scalingMetrics.scaledOffsetX ?? 0;

    const posX = this.viewModel.layoutConfig.boardWidth * 0.15;
    const posY = this.viewModel.layoutConfig.boardHeight * -0.5;

    return new Phaser.Math.Vector2(posX * scaleFactor + scaledOffsetX, posY * scaleFactor);
  }

  private globalBalancePosition(): Phaser.Math.Vector2 {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const scaledOffsetX = this.currentResizeMetrics?.scalingMetrics.scaledOffsetX ?? 0;

    const posX = this.viewModel.layoutConfig.boardWidth * 0.5;
    const posY = this.viewModel.layoutConfig.boardHeight * 1.2;

    return new Phaser.Math.Vector2(posX * scaleFactor + scaledOffsetX, posY * scaleFactor);
  }

  private globalWinChipsPosition(): Phaser.Math.Vector2 {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const scaledOffsetX = this.currentResizeMetrics?.scalingMetrics.scaledOffsetX ?? 0;

    const posX = this.viewModel.layoutConfig.boardWidth * 0.5;
    const posY = this.viewModel.layoutConfig.boardHeight * -0.2;

    return new Phaser.Math.Vector2(posX * scaleFactor + scaledOffsetX, posY * scaleFactor);
  }
}
